/* eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';
import { takeLatest, call, all, put, select } from 'redux-saga/effects';

// Components
import { ModalBulkErrors } from 'components/ModalBulkErrors';

// Services
import { studentsApi } from 'services/apiDigitalTicket/students';
import { appUser } from 'services/apiDigitalTicket/users';
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

// Helpers
import { getId } from 'helpers/auth';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import ENVS from 'constants/enviroments';

// Store
import store from 'store';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { getAppUserRequest } from 'store/ducks/users/actions';
import * as StudentsActions from './actions';

// Types
import { StudentsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof StudentsActions.listStudentsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando estudantes' }),
    );
    const teachingInstitutionId = getId();
    const response = yield call(studentsApi.get, {
      params: { schoolId: teachingInstitutionId, ...payload },
    });

    if (response.status === 200) {
      yield put(
        StudentsActions.listStudentsSuccess({
          studentsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - listStudents - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.listStudentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof StudentsActions.getStudentRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(appUser.get, { params: { ...payload } });

    if (response.status === 200) {
      yield put(
        StudentsActions.getStudentSuccess({ studentData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - getStudent - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.getStudentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof StudentsActions.createStudentRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando estudante' }),
    );
    const response = yield call(studentsApi.create, { data: payload });

    if (response.status === 200) {
      yield put(StudentsActions.createStudentSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const message = errorMessagersList(response.data.error);

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message,
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Estudante cadastrado com sucesso',
          }),
        );
        yield put(
          StudentsActions.listStudentsRequest({
            // @ts-ignore
            schoolId: payload.schoolId,
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - createStudent - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.createStudentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof StudentsActions.updateStudentRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando estudante' }));
    const response = yield call(studentsApi.update, { data: { ...payload } });
    const PATH_URL = window.location.pathname;

    if (response.status === 200) {
      yield put(StudentsActions.updateStudentSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const message = errorMessagersList(response.data.error);

        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message,
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Estudante editado com sucesso',
          }),
        );
        yield put(
          StudentsActions.listStudentsRequest({
            // @ts-ignore
            schoolId: payload.schoolId,
          }),
        );
      }

      if (PATH_URL.includes('/passageiros/')) {
        yield put(
          getAppUserRequest({
            id: PATH_URL.substring(PATH_URL.lastIndexOf('/') + 1),
            _includes: 'company,school,document',
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - updateStudent - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.updateStudentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof StudentsActions.deleteStudentRequest>): any {
  try {
    const { doNotList, body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo estudante' }));
    const response = yield call(studentsApi.delete, { data: { ...body } });

    if (response.status === 200) {
      yield put(StudentsActions.deleteStudentSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Estudante excluído com sucesso',
        }),
      );
      if (!doNotList) {
        yield put(
          StudentsActions.listStudentsRequest({
            schoolId: (body as { schoolId: string }).schoolId,
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - deleteStudent - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.deleteStudentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
}: ReturnType<typeof StudentsActions.createStudentsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando estudantes' }),
    );
    const response = yield call(studentsApi.create, { data: [...payload] });

    if (response.status === 200) {
      yield put(StudentsActions.createStudentsSuccess());

      yield all(
        response.data.success.map(({ email }: any) =>
          call(() =>
            sendPasswordResetEmail(auth, email, {
              url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
            }),
          ),
        ),
      );

      if (response.data.success.length > 0) {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: `${response.data.success.length} Estudante(s) cadastrado(s) com sucesso. E-mail(s) de definição de senha enviado(s).`,
          }),
        );
      }

      if (response.data.error.length > 0) {
        yield put(DialogsActions.setClose());

        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(StudentsActions.listStudentsRequest({}));
            },
          }),
        );
      } else {
        yield put(DialogsActions.setClose());

        yield put(StudentsActions.listStudentsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - createStudents - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.createStudentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateList({
  payload,
}: ReturnType<typeof StudentsActions.updateStudentsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando estudantes' }));
    const response = yield call(studentsApi.update, { data: { ...payload } });

    if (response.status === 200) {
      yield put(StudentsActions.updateStudentsSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(StudentsActions.listStudentsRequest({}));
            },
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Estudantes editados com sucesso',
          }),
        );
        yield put(StudentsActions.listStudentsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - updateStudents - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.updateStudentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeList({
  payload,
}: ReturnType<typeof StudentsActions.deleteStudentsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo estudantes' }));
    const response = yield call(studentsApi.delete, { data: { ...payload } });

    if (response.status === 200) {
      yield put(StudentsActions.deleteStudentsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Estudantes excluídos com sucesso',
        }),
      );
      yield put(StudentsActions.listStudentsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - deleteStudents - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.deleteStudentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* checkUsers({
  payload,
}: ReturnType<typeof StudentsActions.checkStudentsRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const teachingInstitutionId = getId();

    const response = yield call(studentsApi.checkUsers, { data: { transportOperatorId: transportOperator.id, authUserId: teachingInstitutionId ?? '', ...payload } });

    if (response.status === 200) {
      yield put(StudentsActions.checkStudentsSuccess({ studentsCheckUsersList: response.data }));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('students - checkUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(StudentsActions.checkStudentsFailure());
  }
}

// eslint-disable-next-profile import/prefer-default-export
export const studentsSagas = [
  takeLatest(StudentsTypes.LIST_STUDENTS_REQUEST, list),
  takeLatest(StudentsTypes.GET_STUDENT_REQUEST, get),
  takeLatest(StudentsTypes.CREATE_STUDENT_REQUEST, create),
  takeLatest(StudentsTypes.UPDATE_STUDENT_REQUEST, update),
  takeLatest(StudentsTypes.DELETE_STUDENT_REQUEST, remove),
  takeLatest(StudentsTypes.CREATE_STUDENTS_REQUEST, createList),
  takeLatest(StudentsTypes.UPDATE_STUDENTS_REQUEST, updateList),
  takeLatest(StudentsTypes.DELETE_STUDENTS_REQUEST, removeList),
  takeLatest(StudentsTypes.CHECK_STUDENTS_REQUEST, checkUsers),
];
